import { getResourcesById as getBusinesses } from 'app-components/flux/business/businessSelectors';
import type { BUSINESS_REDUCER_KEY } from 'app-components/flux/business/businessTypes';
import { getCategories, getCategoryById } from 'app-components/flux/categories/categoriesSelectors';
import type {
  CATEGORIES_REDUCER_KEY,
  CategoryProps,
  CategoryType,
} from 'app-components/flux/categories/categoriesTypes';
import { type TAX_LINE_ITEMS_REDUCER_KEY, taxLineItemsSelectors } from 'app-components/flux/tax-line-items';
import createForm, { ValidationTrigger } from 'app-components/utils/createForm/createForm';
import type RootState from 'app-components/utils/redux-store/rootState';
import { produce } from 'immer';
import { createSelector } from 'reselect';

type FieldValues = {
  name: string | undefined;
  usageType: 'BUSINESS' | 'PERSONAL';
  type: CategoryType;
  parentId: string | undefined;
  isExcludedFromReports: boolean;
  isExcludedFromBudgets: boolean;
  taxEnabled: boolean;
  taxLineItem: string | undefined;
};

type ExtraKeys = {
  taxLineItem: string;
  taxEnabled: string;
};

type State = CategoryProps & {
  taxEnabled: Record<string, boolean>;
};

const selectHasDuplicateSibling = createSelector(
  getCategories,
  (_state: unknown, { parentId }: CategoryProps) => parentId,
  (_state: unknown, { id }: CategoryProps) => id,
  (_state: unknown, { usageType }: CategoryProps) => usageType,
  (_state: unknown, { name }: CategoryProps) => name?.toLowerCase().trim(),
  (categories, parentId, id, usageType, lowerTrimmedName) =>
    categories.some(
      (category) =>
        category.parentId === parentId &&
        category.id !== id &&
        category.usageType === usageType &&
        category.name?.toLowerCase().trim() === lowerTrimmedName,
    ),
);

const selectHasSameParentName = createSelector(
  getCategories,
  (_state: unknown, { parentId }: CategoryProps) => parentId,
  (_state: unknown, { name }: CategoryProps) => name?.toLowerCase().trim(),
  (categories, parentId, lowerTrimmedName) =>
    categories.find((category) => category.id === parentId && category.name?.toLowerCase().trim() === lowerTrimmedName),
);

const RootCategoryId = '0';
export const selectHasParent = (_store: unknown, s: State) => Boolean(s.parentId && s.parentId !== RootCategoryId);
export const selectTaxLineDescription =
  (taxGroup: string) => (store: Pick<RootState, typeof TAX_LINE_ITEMS_REDUCER_KEY>, s: State) => {
    const taxLineItem = s.txfIds?.[taxGroup];
    if (!taxLineItem) {
      return undefined;
    }
    return taxLineItemsSelectors.getTaxLineItemForId(store, taxLineItem)?.description;
  };
export const selectUsageType = (_store: unknown, s: State) => (s.usageType === 'BUSINESS' ? 'BUSINESS' : 'PERSONAL');
export const selectId = (_store: unknown, s: State) => s.id;

export const getTaxGroups = createSelector(getBusinesses, (businesses) =>
  businesses
    .map((biz) => biz.taxGroup)
    .filter((group): group is string => !!group)
    .toSet()
    .toArray(),
);

const init = (input: CategoryProps, store: Pick<RootState, BUSINESS_REDUCER_KEY>): State => ({
  ...input,
  taxEnabled: Object.fromEntries(getTaxGroups(store).map((group) => [group, !!input.txfIds?.[group]])),
});

const submitTransformer = (state: State): CategoryProps => ({
  ...state,
  txfIds: state.txfIds
    ? Object.fromEntries(Object.entries(state.txfIds).filter(([group]) => state.taxEnabled[group] !== false))
    : undefined,
});

const categoryForm = createForm({
  types: {} as {
    fieldValues: FieldValues;
    extraKeys: ExtraKeys;
    reduxStore: Pick<RootState, CATEGORIES_REDUCER_KEY | typeof TAX_LINE_ITEMS_REDUCER_KEY | BUSINESS_REDUCER_KEY>;
  },
  reducer: (state, action, store) =>
    produce(state, (draft) => {
      switch (action.t) {
        case 'valueChange':
          switch (action.field) {
            case 'name':
              draft.name = action.value;
              break;
            case 'usageType':
              draft.usageType = action.value;
              break;
            case 'type':
              draft.type = action.value;
              break;
            case 'parentId': {
              draft.parentId = action.value;
              const parent = action.value
                ? getCategoryById(store as unknown as Pick<RootState, CATEGORIES_REDUCER_KEY>, action.value)
                : undefined;
              if (parent) {
                draft.type = parent.type;
                draft.usageType = parent.usageType;
              }
              break;
            }
            case 'isExcludedFromReports':
              draft.isExcludedFromReports = action.value;
              break;
            case 'isExcludedFromBudgets':
              draft.isExcludedFromBudgets = action.value;
              break;
            case 'taxEnabled':
              draft.taxEnabled[action.key] = action.value;
              break;
            case 'taxLineItem':
              if (!draft.txfIds) {
                draft.txfIds = {};
              }
              if (action.value) {
                draft.txfIds[action.key] = action.value;
              } else {
                delete draft.txfIds[action.key];
              }
              break;
          }
      }
    }),
  init,
  fieldConfigs: {
    name: {
      selector: ({ name }) => name,
      validation: {
        required: {
          triggers: ValidationTrigger.onSubmit,
          validate({ name }, _store) {
            if (!name) {
              return 'Required';
            }

            return undefined;
          },
        },
        noDuplicateSibling: {
          triggers: ValidationTrigger.onChange,
          validate(state, store) {
            if (selectHasDuplicateSibling(store as unknown as Pick<RootState, CATEGORIES_REDUCER_KEY>, state)) {
              return 'Name cannot be same as another category with same parent';
            }

            return undefined;
          },
        },
        differentNameThanParent: {
          triggers: ValidationTrigger.onChange,
          validate(state, store) {
            if (selectHasSameParentName(store as unknown as Pick<RootState, CATEGORIES_REDUCER_KEY>, state)) {
              return 'Category and subcategory names must be different';
            }

            return undefined;
          },
        },
      },
    },
    usageType: {
      selector: ({ usageType }) => (usageType === 'BUSINESS' ? 'BUSINESS' : 'PERSONAL'),
    },
    type: {
      selector: ({ type }) => type || 'EXPENSE',
    },
    parentId: {
      selector: ({ parentId }) => parentId,
    },
    isExcludedFromReports: {
      selector: ({ isExcludedFromReports }) => !!isExcludedFromReports,
    },
    isExcludedFromBudgets: {
      selector: ({ isExcludedFromBudgets }) => !!isExcludedFromBudgets,
    },
    taxEnabled: {
      selector: ({ taxEnabled }, group) => !!taxEnabled[group],
      selectActiveKeys: ({ taxEnabled }) => Object.keys(taxEnabled),
    },
    taxLineItem: {
      selector: ({ txfIds }, group) => txfIds?.[group],
      selectActiveKeys: ({ taxEnabled }) => Object.keys(taxEnabled),
      validation: {
        required: {
          triggers: ValidationTrigger.onSubmit,
          validate({ taxEnabled, txfIds }, _store, key) {
            return taxEnabled[key] && !txfIds?.[key] ? 'Required' : undefined;
          },
        },
      },
    },
  },
  submitTransformer,
});

export const { Provider: CategoryFormProvider, ...categoryFormHooks } = categoryForm.createManagerProvider();

export default categoryForm;
